import React, { useEffect, useRef, useState } from "react";

import cx from "classnames";
import { useRouter } from "next/router";
import { CgClose } from "react-icons/cg";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";

import { Portalize } from "components/portalize";
import { useClickOutside } from "hooks/use-click-outside";

const TIMEOUT_IN_MS = 200;

type TransitionClasses = {
  [K in TransitionStatus]: string;
};

const TRANSITION_CLASSES: TransitionClasses = {
  entering: "ease-out duration-300 opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
  entered: "ease-out duration-300 opacity-100 translate-y-0 sm:scale-100",
  exiting: "ease-in duration-200 opacity-100 translate-y-0 sm:scale-100",
  exited: "ease-in duration-200 opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
  unmounted: "",
};

export interface ModalProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  isOpen: boolean;
  showCloseButton?: boolean;
  onRequestClose: () => void;
  onCloseComplete?: () => void;
  onVisibleCallback?: () => void;
  closeOnOutsideClick?: boolean;
}

export const Modal: React.FC<React.PropsWithChildren<Readonly<ModalProps>>> = ({
  className,
  isOpen = false,
  showCloseButton = false,
  onRequestClose,
  onCloseComplete,
  onVisibleCallback,
  closeOnOutsideClick = false,
  children,
  ...props
}) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const router = useRouter();

  const addOverflowClass = (): void => {
    document.body.classList.add("overflow-auto");
  };
  const removeOverflowClass = (): void => {
    document.body.classList.remove("overflow-auto");
  };

  useEffect(() => {
    router.events.on("routeChangeComplete", removeOverflowClass);
    return () => {
      router.events.off("routeChangeComplete", removeOverflowClass);
    };
  }, [router]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      addOverflowClass();
      setIsVisible(true);
      if (onVisibleCallback) {
        onVisibleCallback();
      }
      return removeOverflowClass;
    }

    // Hide the modal after a timeout to give us time to perform the exit
    // transitions.
    setTimeout(() => {
      setIsVisible(false);
      document.body.classList.remove("overflow-auto");

      if (onCloseComplete) {
        onCloseComplete();
      }
    }, TIMEOUT_IN_MS * 2);
    return removeOverflowClass;
  }, [isOpen, onCloseComplete, onVisibleCallback]);

  useClickOutside(modalRef, () => {
    if (closeOnOutsideClick && typeof onRequestClose === "function") {
      onRequestClose();
    }
  });

  // Since we're rendering the modal inside of a portal we need to make sure we
  // only render something once we're rendering on the client (when `isMounted`
  // is `true`).
  if (!isMounted) {
    return null;
  }

  return (
    <Portalize>
      {/* @ts-ignore - React 18/19 type incompatibility with react-transition-group */}
      <Transition in={isOpen} timeout={TIMEOUT_IN_MS}>
        {/* @ts-ignore - React 18/19 type incompatibility with react-transition-group */}
        {(state) =>
          isVisible ? (
            <div {...props} className={cx("fixed z-10 inset-0 overflow-y-auto", className, TRANSITION_CLASSES[state])}>
              <div className="flex items-end justify-center max-w-full min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity">
                  <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>
                <div ref={modalRef} className="inline-block w-full my-10 text-left max-w-screen-sm transform sm:my-32">
                  {showCloseButton && (
                    <div className="absolute top-4 right-4" onClick={onRequestClose}>
                      <CgClose className="w-6 h-6 text-gray-600 cursor-pointer" />
                    </div>
                  )}
                  {children}
                </div>
              </div>
            </div>
          ) : null
        }
      </Transition>
    </Portalize>
  );
};
